import React, { useState } from 'react';
import { PlusIcon, TrashIcon, SearchIcon, InfoIcon, FilterIcon } from 'lucide-react';
// Mock parts data
const mockPartsData = [{
  id: 'P001',
  reference: 'AIS-101-A',
  name: 'Control Panel',
  category: 'Electronics',
  unitPrice: 1250
}, {
  id: 'P002',
  reference: 'AIS-202-B',
  name: 'Gear Drive',
  category: 'Mechanical',
  unitPrice: 850
}, {
  id: 'P003',
  reference: 'AIS-303-C',
  name: 'Sprinkler Head',
  category: 'Irrigation',
  unitPrice: 45
}, {
  id: 'P004',
  reference: 'AIS-404-D',
  name: 'Drive Motor',
  category: 'Mechanical',
  unitPrice: 1100
}, {
  id: 'P005',
  reference: 'AIS-505-E',
  name: 'Pressure Gauge',
  category: 'Sensors',
  unitPrice: 75
}, {
  id: 'P006',
  reference: 'AIS-606-F',
  name: 'Solenoid Valve',
  category: 'Irrigation',
  unitPrice: 120
}, {
  id: 'P007',
  reference: 'AIS-707-G',
  name: 'GPS Module',
  category: 'Electronics',
  unitPrice: 550
}, {
  id: 'P008',
  reference: 'AIS-808-H',
  name: 'Tower Box',
  category: 'Structural',
  unitPrice: 350
}, {
  id: 'P009',
  reference: 'AIS-909-I',
  name: 'Span Pipe',
  category: 'Structural',
  unitPrice: 420
}, {
  id: 'P010',
  reference: 'AIS-010-J',
  name: 'End Gun',
  category: 'Irrigation',
  unitPrice: 980
}];
export function PartsSelectionStep({
  initialData = [],
  onUpdate
}) {
  const [selectedParts, setSelectedParts] = useState(initialData);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showPartsSelector, setShowPartsSelector] = useState(false);
  // Filter parts based on search and category
  const filteredParts = mockPartsData.filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchQuery.toLowerCase()) || part.reference.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || part.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  // Get unique categories for filter dropdown
  const categories = ['all', ...new Set(mockPartsData.map(part => part.category))];
  const handleAddPart = part => {
    // Check if part is already selected
    const existingPartIndex = selectedParts.findIndex(p => p.id === part.id);
    if (existingPartIndex >= 0) {
      // If already selected, increase quantity
      const newParts = [...selectedParts];
      newParts[existingPartIndex].quantity += 1;
      setSelectedParts(newParts);
      onUpdate(newParts);
    } else {
      // Otherwise add as new with quantity 1
      const newParts = [...selectedParts, {
        ...part,
        quantity: 1
      }];
      setSelectedParts(newParts);
      onUpdate(newParts);
    }
  };
  const handleRemovePart = index => {
    const newParts = selectedParts.filter((_, i) => i !== index);
    setSelectedParts(newParts);
    onUpdate(newParts);
  };
  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newParts = [...selectedParts];
    newParts[index].quantity = newQuantity;
    setSelectedParts(newParts);
    onUpdate(newParts);
  };
  // Calculate total
  const total = selectedParts.reduce((sum, part) => sum + part.unitPrice * part.quantity, 0);
  return <div className="space-y-6" data-id="element-4936">
      <div className="text-white text-xl font-medium mb-6" data-id="element-4937">
        Select Spare Parts
      </div>
      <div className="bg-blue-900/30 rounded-xl p-5 border border-blue-800/30 mb-6" data-id="element-4938">
        <div className="flex items-start" data-id="element-4939">
          <InfoIcon size={20} className="text-blue-400 mr-3 mt-0.5" data-id="element-4940" />
          <div data-id="element-4941">
            <h3 className="text-white font-medium mb-1" data-id="element-4942">
              Spare Parts Selection Guide
            </h3>
            <p className="text-blue-200 text-sm" data-id="element-4943">
              Select the spare parts you need for your irrigation system. You
              can search by name or reference number and filter by category. Add
              multiple items and adjust quantities as needed.
            </p>
          </div>
        </div>
      </div>

      {/* Selected Parts List */}
      <div className="bg-slate-800/50 rounded-xl border border-blue-800/30" data-id="element-4944">
        <div className="flex justify-between items-center p-4 border-b border-blue-800/30" data-id="element-4945">
          <h3 className="text-white font-medium" data-id="element-4946">Selected Parts</h3>
          <button onClick={() => setShowPartsSelector(true)} className="flex items-center px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors" data-id="element-4947">
            <PlusIcon size={16} className="mr-1" data-id="element-4948" />
            Add Parts
          </button>
        </div>
        {selectedParts.length > 0 ? <div data-id="element-4949">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-blue-800/30 text-sm font-medium text-blue-300" data-id="element-4950">
              <div className="col-span-1" data-id="element-4951">Ref</div>
              <div className="col-span-5" data-id="element-4952">Part Name</div>
              <div className="col-span-2" data-id="element-4953">Category</div>
              <div className="col-span-1 text-center" data-id="element-4954">Qty</div>
              <div className="col-span-2 text-right pr-8" data-id="element-4955">Price</div>
              <div className="col-span-1" data-id="element-4956"></div>
            </div>
            <div className="p-2" data-id="element-4957">
              {selectedParts.map((part, index) => <div key={index} className="grid grid-cols-12 gap-4 p-2 items-center" data-id="element-4958">
                  <div className="col-span-1 text-blue-300 text-sm" data-id="element-4959">
                    {part.reference}
                  </div>
                  <div className="col-span-5 text-white" data-id="element-4960">{part.name}</div>
                  <div className="col-span-2 text-blue-300 text-sm" data-id="element-4961">
                    {part.category}
                  </div>
                  <div className="col-span-1" data-id="element-4962">
                    <div className="flex items-center justify-center" data-id="element-4963">
                      <button onClick={() => handleQuantityChange(index, part.quantity - 1)} className="w-6 h-6 bg-slate-900/50 text-blue-300 rounded-l-md hover:bg-slate-900 hover:text-white" data-id="element-4964">
                        -
                      </button>
                      <input type="number" value={part.quantity} onChange={e => handleQuantityChange(index, parseInt(e.target.value) || 1)} className="w-10 h-6 bg-slate-900/50 border-y border-blue-800/30 text-white text-center focus:outline-none" min="1" data-id="element-4965" />
                      <button onClick={() => handleQuantityChange(index, part.quantity + 1)} className="w-6 h-6 bg-slate-900/50 text-blue-300 rounded-r-md hover:bg-slate-900 hover:text-white" data-id="element-4966">
                        +
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2 text-right text-white pr-8" data-id="element-4967">
                    ${(part.unitPrice * part.quantity).toFixed(2)}
                  </div>
                  <div className="col-span-1 flex justify-center" data-id="element-4968">
                    <button onClick={() => handleRemovePart(index)} className="p-1.5 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-white transition-colors" data-id="element-4969">
                      <TrashIcon size={16} data-id="element-4970" />
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="p-4 border-t border-blue-800/30 flex justify-end" data-id="element-4971">
              <div className="w-64" data-id="element-4972">
                <div className="flex justify-between items-center text-blue-300 mb-2" data-id="element-4973">
                  <span data-id="element-4974">Subtotal:</span>
                  <span className="text-white" data-id="element-4975">${total.toFixed(2)}</span>
                </div>
                <div className="border-t border-blue-800/30 pt-2 mt-2" data-id="element-4976">
                  <div className="flex justify-between items-center" data-id="element-4977">
                    <span className="font-medium text-white" data-id="element-4978">Total:</span>
                    <span className="text-xl font-bold text-white" data-id="element-4979">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="p-8 text-center" data-id="element-4980">
            <p className="text-blue-300 mb-3" data-id="element-4981">No parts selected yet</p>
            <button onClick={() => setShowPartsSelector(true)} className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors" data-id="element-4982">
              <PlusIcon size={18} className="mr-2" data-id="element-4983" />
              Select Parts
            </button>
          </div>}
      </div>

      {/* Parts Selector Modal */}
      {showPartsSelector && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-4984">
          <div className="bg-slate-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-4985">
            <div className="p-4 border-b border-blue-800/30 flex justify-between items-center" data-id="element-4986">
              <h3 className="text-lg font-medium text-white" data-id="element-4987">Select Parts</h3>
              <button onClick={() => setShowPartsSelector(false)} className="text-blue-300 hover:text-white" data-id="element-4988">
                <span className="text-2xl" data-id="element-4989">&times;</span>
              </button>
            </div>
            <div className="p-4 border-b border-blue-800/30 bg-slate-800/50 flex flex-wrap gap-4" data-id="element-4990">
              <div className="relative flex-1 min-w-[250px]" data-id="element-4991">
                <SearchIcon size={18} className="absolute left-3 top-2.5 text-blue-400" data-id="element-4992" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900/70 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search by name or reference..." data-id="element-4993" />
              </div>
              <div className="relative min-w-[200px]" data-id="element-4994">
                <FilterIcon size={18} className="absolute left-3 top-2.5 text-blue-400" data-id="element-4995" />
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="w-full bg-slate-900/70 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238BABBF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              backgroundSize: '20px'
            }} data-id="element-4996">
                  {categories.map(category => <option key={category} value={category} data-id="element-4997">
                      {category === 'all' ? 'All Categories' : category}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="overflow-auto flex-1" data-id="element-4998">
              <table className="w-full border-collapse" data-id="element-4999">
                <thead className="bg-slate-800 sticky top-0" data-id="element-5000">
                  <tr data-id="element-5001">
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5002">
                      Reference
                    </th>
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5003">
                      Name
                    </th>
                    <th className="text-left p-3 text-blue-300 font-medium" data-id="element-5004">
                      Category
                    </th>
                    <th className="text-right p-3 text-blue-300 font-medium" data-id="element-5005">
                      Price
                    </th>
                    <th className="p-3" data-id="element-5006"></th>
                  </tr>
                </thead>
                <tbody data-id="element-5007">
                  {filteredParts.map(part => <tr key={part.id} className="border-b border-blue-900/30 hover:bg-slate-800/50" data-id="element-5008">
                      <td className="p-3 text-blue-300" data-id="element-5009">{part.reference}</td>
                      <td className="p-3 text-white" data-id="element-5010">{part.name}</td>
                      <td className="p-3 text-blue-300" data-id="element-5011">{part.category}</td>
                      <td className="p-3 text-white text-right" data-id="element-5012">
                        ${part.unitPrice.toFixed(2)}
                      </td>
                      <td className="p-3 text-right" data-id="element-5013">
                        <button onClick={() => handleAddPart(part)} className="px-3 py-1 bg-blue-600/30 text-blue-300 rounded hover:bg-blue-600 hover:text-white transition-colors" data-id="element-5014">
                          Add
                        </button>
                      </td>
                    </tr>)}
                  {filteredParts.length === 0 && <tr data-id="element-5015">
                      <td colSpan={5} className="p-6 text-center text-blue-300" data-id="element-5016">
                        No parts found matching your search criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-blue-800/30 bg-slate-800/50 flex justify-end" data-id="element-5017">
              <button onClick={() => setShowPartsSelector(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-5018">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}